import React from 'react';
import { useTranslation } from 'react-i18next';
import { PackagingItem } from '../../models/Packaging';
import { PackagingCard } from '../display';

interface PackagingGridProps {
  items: PackagingItem[];
  error: unknown;
  isFetchingNextPage: boolean;
  lastElementRef: ((node: HTMLDivElement | null) => void) | null;
  onEdit: (item: PackagingItem) => void;
  onDelete: (item: PackagingItem) => void;
  onAdjustStock: (item: PackagingItem) => void;
  /** Si hay más páginas en API pero el filtro local vació la vista, sentinela para seguir cargando */
  hasNextPage?: boolean;
}

const PackagingGrid: React.FC<PackagingGridProps> = ({
  items,
  error,
  isFetchingNextPage,
  lastElementRef,
  onEdit,
  onDelete,
  onAdjustStock,
  hasNextPage = false,
}) => {
  const { t } = useTranslation('materialAlmacenModule');

  if (error) {
    return null;
  }

  if (items.length === 0) {
    if (hasNextPage && lastElementRef) {
      return (
        <>
          <div
            ref={lastElementRef}
            className="h-2 w-full shrink-0"
            aria-hidden
            data-testid="warehouse-material-infinite-scroll-sentinel"
          />
          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-6">
              <div className="text-muted-foreground text-sm" data-testid="loading-more-indicator">
                {t('loading.default')}
              </div>
            </div>
          )}
        </>
      );
    }
    return null;
  }

  return (
    <>
      {/* Grid de materiales con infinite scroll */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((item, index) => (
          <div key={item.id} ref={index === items.length - 1 ? lastElementRef : null}>
            <PackagingCard item={item} onEdit={onEdit} onDelete={onDelete} onAdjustStock={onAdjustStock} />
          </div>
        ))}
      </div>

      {/* Indicador de carga para más elementos */}
      {isFetchingNextPage && (
        <div className="flex justify-center items-center py-6">
          <div className="text-muted-foreground text-sm" data-testid="loading-more-indicator">
            {t('loading.default')}
          </div>
        </div>
      )}
    </>
  );
};

export default PackagingGrid;
